import { useMemo, useState } from "react";
import { Play, Clock, Search } from "lucide-react";
import ReactSelect, { SingleValue } from "react-select";
import PageHeader from "../../components/PageHeader";
import { SELECT_STYLES } from "../../components/DateFilters/constants";
import { EntregadorProvider, useEntregador } from "./context/EntregadorContext";
import IniciarDia from "./IniciarDia";
import Operando from "./Operando";

const EntregadorContent = () => {
  const [activeTab, setActiveTab] = useState<"iniciar" | "operando">("iniciar");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const {
    lojaId,
    lojas,
    selectedLojaId,
    setSelectedLojaId,
    showLojaSelector,
    isLoading,
    searchTerm,
    setSearchTerm,
  } = useEntregador();

  const lojaOptions = useMemo(
    () => lojas.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojas]
  );
  const selectedLojaOption =
    lojaOptions.find((o) => o.value === selectedLojaId) ?? null;

  if (isLoading) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <PageHeader
          title="Entregadores"
          description="Gerencie os entregadores da sua loja"
        />
        <div className="mt-6 flex items-center justify-center">
          <p className="text-neutral-500">Carregando...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4">
        <PageHeader
          title="Entregadores"
          description="Gerencie os entregadores da sua loja"
        />
      </div>

      <div className="mt-6">
        {showLojaSelector && lojas.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <label
              htmlFor="entregador-loja-select"
              className="text-sm font-medium text-neutral-700"
            >
              Loja:
            </label>
            <ReactSelect<{ value: number; label: string }>
              inputId="entregador-loja-select"
              value={selectedLojaOption}
              onChange={(
                option: SingleValue<{ value: number; label: string }>
              ) => setSelectedLojaId(option?.value ?? undefined)}
              options={lojaOptions}
              isClearable
              placeholder="Selecione uma loja"
              className="w-full min-w-[180px] sm:w-56"
              styles={SELECT_STYLES}
            />
          </div>
        )}
        {showLojaSelector && !lojaId && (
          <p className="mb-4 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-800">
            Selecione uma loja para gerenciar os entregadores.
          </p>
        )}
        <div className="flex items-center gap-2">
          <nav className="flex flex-1 overflow-hidden rounded-lg border border-neutral-200 shadow-sm sm:w-fit sm:flex-none">
            <button
              onClick={() => setActiveTab("iniciar")}
              className={`flex flex-1 items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors sm:flex-none sm:px-6 ${
                activeTab === "iniciar"
                  ? "bg-primary text-white"
                  : "bg-neutral-50 text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800"
              }`}
            >
              <Play size={16} />
              <span className="hidden sm:inline">Iniciar Dia</span>
            </button>
            <button
              onClick={() => setActiveTab("operando")}
              className={`flex flex-1 items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors sm:flex-none sm:px-6 ${
                activeTab === "operando"
                  ? "bg-primary text-white"
                  : "bg-neutral-50 text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800"
              }`}
            >
              <Clock size={16} />
              <span className="hidden sm:inline">Operando</span>
            </button>
          </nav>

          <div className="relative flex shrink-0 items-center sm:ml-2 sm:flex-1">
            {!isSearchExpanded ? (
              <button
                onClick={() => setIsSearchExpanded(true)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-neutral-300 bg-white text-neutral-400 transition-all hover:border-primary hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Buscar entregador"
              >
                <Search size={20} />
              </button>
            ) : (
              <div className="animate-in fade-in slide-in-from-right-2 relative w-full">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="h-5 w-5 text-neutral-400" size={20} />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onBlur={() => {
                    if (!searchTerm) {
                      setIsSearchExpanded(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setSearchTerm("");
                      setIsSearchExpanded(false);
                    }
                  }}
                  placeholder="Buscar por nome..."
                  autoFocus
                  className="block w-full rounded-lg border border-neutral-300 bg-white py-2 pl-10 pr-10 text-sm placeholder-neutral-500 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary sm:text-base"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setIsSearchExpanded(false);
                    }}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 transition-colors hover:text-neutral-600"
                    aria-label="Limpar busca"
                  >
                    <span className="text-lg leading-none">×</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          {activeTab === "iniciar" && <IniciarDia />}
          {activeTab === "operando" && <Operando />}
        </div>
      </div>
    </main>
  );
};

const Entregador = () => {
  return (
    <EntregadorProvider>
      <EntregadorContent />
    </EntregadorProvider>
  );
};

export default Entregador;
